import {
  autocompletion,
  Completion,
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete';

import { ExpressionVariable } from './expressionCompletion';

/**
 * Creates an autocompletion extension for expr-lang variables.
 * Triggers completion when user types identifiers (word characters and dots).
 * Does NOT wrap variables in {{}} - uses bare variable paths.
 *
 * @param variables - Array of available expression variables
 * @returns CodeMirror autocompletion extension
 */
export function exprCompletion(variables: ExpressionVariable[]) {
  return autocompletion({
    override: [
      (context: CompletionContext): CompletionResult | null => {
        // Match identifier path (word characters and dots for property access)
        const match = context.matchBefore(/[\w.]+$/);

        // If no match and not explicit activation, return null
        if (!match && !context.explicit) return null;

        const partialPath = match?.text || '';
        const from = match?.from ?? context.pos;

        // Filter and map variables to completion options
        const options: Completion[] = variables
          .filter((v) =>
            v.path.toLowerCase().includes(partialPath.toLowerCase()),
          )
          .map((v) => ({
            label: v.path,
            type: 'variable',
            detail: `${v.label} (${v.type})`,
          }));

        if (options.length === 0) return null;

        return {
          from,
          options,
          filter: false, // We already filtered
        };
      },
    ],
    icons: true,
  });
}
